/**
 * @file GoldenRetriever recovery helpers: legacy ID migration, metadata persistence, blob restore.
 */

import type Uppy from '@uppy/core';
import type { UppyFile } from '@uppy/core';
import IndexedDBStore from '@uppy/golden-retriever/lib/IndexedDBStore.js';
import MetaDataStore from '@uppy/golden-retriever/lib/MetaDataStore.js';

import { UPPY_INSTANCE_ID } from './constants';

/** Default Uppy id before we pinned {@link UPPY_INSTANCE_ID}. */
export const LEGACY_UPPY_INSTANCE_ID = 'uppy';

type Meta = Record<string, unknown>;
type Body = Record<string, unknown>;

const metaDataStore = new MetaDataStore({ storeName: UPPY_INSTANCE_ID });

let legacyBlobStore: IndexedDBStore | null = null;

const getLegacyBlobStore = (): IndexedDBStore => {
  if (!legacyBlobStore) {
    legacyBlobStore = new IndexedDBStore({
      storeName: LEGACY_UPPY_INSTANCE_ID,
    });
  }

  return legacyBlobStore;
};

/** Copy GoldenRetriever metadata from the legacy `uppy` instance id if needed. */
export const migrateLegacyGoldenRetrieverMetadata = (): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const targetKey = `uppyState:${UPPY_INSTANCE_ID}`;
  if (localStorage.getItem(targetKey)) {
    return;
  }

  const legacyKey = `uppyState:${LEGACY_UPPY_INSTANCE_ID}`;
  const legacyState = localStorage.getItem(legacyKey);
  if (!legacyState) {
    return;
  }

  localStorage.setItem(targetKey, legacyState);
};

const serializeFileForRecovery = (file: UppyFile<Meta, Body>) =>
  file.isRemote
    ? { ...file, isRestored: true }
    : { ...file, isRestored: true, data: null, preview: null };

/**
 * GoldenRetriever only persists files in `currentUploads` or waiting to start.
 * During active XHR uploads the file can fall through that gap — persist all
 * incomplete files so reload recovery has metadata to restore.
 */
export const persistIncompleteUploadMetadata = (
  uppy: Uppy<Meta, Body>
): void => {
  const incompleteFiles = uppy
    .getFiles()
    .filter((file) => !file.progress?.uploadComplete);

  if (incompleteFiles.length === 0) {
    return;
  }

  const filesToSaveWithoutData = Object.fromEntries(
    incompleteFiles.map((file) => [file.id, serializeFileForRecovery(file)])
  );

  metaDataStore.save({
    currentUploads: uppy.getState().currentUploads,
    files: filesToSaveWithoutData,
    pluginData: {},
  });
};

const throttlePersist = (uppy: Uppy<Meta, Body>) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return () => {
    if (timer) {
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      persistIncompleteUploadMetadata(uppy);
    }, 500);
  };
};

/** Wires throttled metadata persistence on Uppy file/progress events. */
export const installIncompleteUploadPersistence = (
  uppy: Uppy<Meta, Body>
): void => {
  const persist = throttlePersist(uppy);

  uppy.on('file-added', persist);
  uppy.on('upload-progress', persist);
  uppy.on('state-update', persist);
};

/** Attach blobs saved under the legacy `uppy` IndexedDB store after metadata restore. */
export const recoverLegacyIndexedDBBlobs = async (
  uppy: Uppy<Meta, Body>
): Promise<void> => {
  const filesNeedingBlobs = uppy
    .getFiles()
    .filter((file) => file.data == null || file.isGhost);

  if (filesNeedingBlobs.length === 0) {
    return;
  }

  let legacyBlobs: Record<string, Blob> = {};
  try {
    legacyBlobs = await getLegacyBlobStore().list();
  } catch {
    return;
  }

  filesNeedingBlobs.forEach((file) => {
    const blob = legacyBlobs[file.id];
    if (!blob) {
      return;
    }

    uppy.setFileState(file.id, {
      data: blob,
      isGhost: false,
      isRestored: true,
    });
  });
};
